import React, { lazy, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import * as serviceWorker from "./serviceWorker";
import { Route, Switch, Router } from "react-router-dom";
import SignUp from "./routers/SignUp";
import ForgotPassword from "./routers/ForgotPassword";
import ResetPassword from "./routers/ResetPassword";
import PortfolioFrame from "./routers/PortfolioFrame/index";
import DownloadPage from "./routers/DownloadPage";
import DownloadPageDashboard from "./routers/DownloadPageDashboard";
import DownloadPageInsights from "./routers/DownloadPageInsights";
import Payment from "./routers/Payment";
import Feedback from "./routers/Feedback";
import LinkRedirection from "./routers/LinkRedirection";
import MicrosoftLogin from "./routers/MicrosoftLogin";
import PrivacyPolicy from "./routers/PrivacyPolicy";
import AcceptAuth from "./routers/AcceptAuth";
import TermsAndPolicy from "./routers/TermsAndPolicy";
import ProtectedRoute from "./components/ProtectedRoute";
import history from "./history";
import "antd/dist/antd.css";
import "font-awesome/scss/font-awesome.scss";
import "bootstrap/dist/css/bootstrap.min.css";
import "../public/css/style.css";
import "./index.scss";
import { Suspense } from "react";
import ResetPasswordExpired from "./routers/ResetPasswordExpired";
import CreateWorkFlow from "./routers/CreateWorkFlow";
import BuildProfile from "./routers/BuildNewProfile";
import LoginVerification from "./routers/LoginVerification";
import DashboardReviewPdf from "./routers/DownloadReviewDashboard";
import DownloadPerformanceInsights from "./routers/DownloadPerformanceInsights";
import { urlName } from "./config";
import GetDomainDetailsAction from "./flux/dispatcher/action/GetDomainDetailsAction";
import GetDomainDetailsStore from "./flux/dispatcher/store/GetDomainDetailsStore";
import config from "../globalconfig";
function lazyWithPreload(factory) {
	const Component = lazy(factory);
	Component.preload = factory;
	return Component;
}
const Login = lazy(() => import("./routers/Login"));
const AuthorisePage = lazy(() => import("./routers/AuthorisePage"));
const AuthorisePageDemo = lazy(() => import("./routers/AuthorizePage"));
const AccountOverView = lazy(() => import("./routers/AccountOverView"));
document.body.classList.add("light");
export const MainFrame = lazyWithPreload(() => import("./routers/MainFrame"));

function AppLoader() {
	const [isReady, setIsReady] = useState(false);

	useEffect(() => {
		// Bind the store listener
		GetDomainDetailsStore.bind(handleDomainDetails);
		document.title =  urlName;
		// Trigger fetch
		GetDomainDetailsAction.get(urlName);

		// Cleanup on unmount
		return () => {
			GetDomainDetailsStore.unbind(handleDomainDetails);
		};
	}, []);
	const handleDomainDetails = () => {
		const domainData = GetDomainDetailsStore.getResponse();
		if (!domainData) return;

		// 1️⃣ Set document title
		document.title = domainData.title;	

		// 2️⃣ Set global allowedDomains for HTML/JS usage
		window.allowedDomains = domainData.domainList || false;
		window.domainReady = true; // flag that data is ready

		// 3️⃣ Dispatch event so HTML can listen
		window.dispatchEvent(new Event("domainDataReady"));

		// 4️⃣ Set favicon dynamically
		let link =
			document.querySelector("link[rel*='icon']") ||
			document.createElement("link");
		link.type = "image/x-icon";
		link.rel = "shortcut icon";
		link.href = domainData.href || "/favicon.ico";
		document.head.appendChild(link);

		// 5️⃣ Set meta description
		let meta =
			document.querySelector('meta[name="description"]') ||
			document.createElement("meta");
		meta.name = "description";
		meta.content = domainData.description || "";
		document.head.appendChild(meta);

		// 6️⃣ Optional HubSpot script injection
		if (urlName === domainData.htmlScript && !window.location.href.includes("token=")) {
			let script = document.createElement("script");
			script.type = "text/javascript";
			script.id = "hs-script-loader";
			script.src = "https://js.hs-scripts.com/3516618.js";
			document.head.appendChild(script);
		}

		// 7️⃣ Store language in localStorage if not already set
		if (!localStorage.getItem("language") && domainData.language) {
			localStorage.setItem("language", domainData.language);
		}

		// 8️⃣ Mark React app as ready to render
		setIsReady(true);
	};

	if (!isReady) {
		return null;
	}

	// 🔹 Once ready, render main app
	return <RootApp />;
}

function RootApp() {
	return (
		<Router history={history}>
			<Suspense fallback>
				<Switch>
					<Route
						exact
						path="/login"
						component={Login}
						onEnter
					/>
					<Route
						path="/login/verification"
						component={LoginVerification}
					/>
					<Route
						path="/signup"
						component={SignUp}
					/>
					<Route
						path="/forgotPassword"
						component={ResetPassword}
					/>
					<Route
						path="/paymentexpired"
						component={Payment}
					/>
					<Route
						path="/feedback"
						component={Feedback}
					/>
					<Route
						path="/routegate"
						component={LinkRedirection}
					/>
					<Route
						path="/redirect/:query"
						component={SignUp}
					/>
					<Route
						path="/acceptAuth"
						component={AcceptAuth}
					/>
					<Route
						path="/passwordReset/:query"
						component={ForgotPassword}
					/>
					<Route
						path="/downloadPdf/:query"
						component={DownloadPage}
					/>
					
					<Route
						path="/reviewPdf/:query"
						component={DashboardReviewPdf}
					/>
					<Route
						path="/insightPdf/:query"
						component={DownloadPerformanceInsights}
					/>
					<Route
						path="/microsoftRedirect/:query"
						component={MicrosoftLogin}
					/>
					<Route
						path="/privacy"
						component={PrivacyPolicy}
					/>
					<Route
						path="/terms"
						component={TermsAndPolicy}
					/>
					<Route
						path="/policy"
						component={PrivacyPolicy}
					/>
					{/* <Route path='/resetPassword' component={ForgotPassword} /> */}
					<Route
						path="/resetPasswordLinkExpired"
						component={ResetPasswordExpired}
					/>
					{/* <ProtectedRoute path='/authorize' component={AuthorisePage} /> */}
					<ProtectedRoute
						path="/new-workspace"
						component={CreateWorkFlow}
					/>
					<ProtectedRoute
						path="/build-profile"
						component={BuildProfile}
					/>
					<ProtectedRoute
						path="/account-overview"
						component={AccountOverView}
					/>
					<ProtectedRoute
						path="/authorize"
						component={AuthorisePageDemo}
					/>
					<ProtectedRoute
						path="/helpdesk"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/realitycheck"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/dashboard"
						component={MainFrame}
					/>
					{/* <ProtectedRoute
					path='/localperformance'
					component={MainFrame}
				/> */}
					<ProtectedRoute
						path="/insights"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/reviews"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/posts"
						component={MainFrame}
					/>
					<ProtectedRoute
						exact
						path="/imagemanagement"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/widgetshowcase"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/autoresponse"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/qareviews"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/settings"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/localpost"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/competitor"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/iframe"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/managelocations"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/facebookdashboard"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/sentimentanalysis"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/locationbulkupdate"
						component={MainFrame}
					/>
					<ProtectedRoute
						path="/manageusers"
						component={PortfolioFrame}
					/>
					<ProtectedRoute
						path="/manageprofiles"
						component={PortfolioFrame}
					/>
					<ProtectedRoute
						path="/portfoliosettings"
						component={PortfolioFrame}
					/>
					<ProtectedRoute component={AuthorisePageDemo} />
				</Switch>
			</Suspense>
		</Router>
	);
}
ReactDOM.render(<AppLoader />, document.getElementById("root"));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
